import * as React from "react";
import { cn } from "./cn";

/**
 * Technical details — a per-viewer switch for what customers do not need and power users want:
 * which server a figure came from, the system unit an app runs as, a raw check id, a Sentinel name.
 *
 * Default OFF in the customer consoles (owner, 2026-11-02: "behind a toggle; do not delete them").
 * Nothing is removed from the page source: wrapped in <Technical>, it renders only when the viewer
 * has asked to see it. The choice is remembered per browser, like the theme.
 *
 * A module-level store rather than a provider: three consoles mount this, and a switch that needs
 * a provider in each is a switch that silently does nothing in the one that forgot it.
 */
const KEY = "ozpanel.technical";
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function setShowTechnical(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* storage disabled: the switch still works for this page view */
  }
  current = on;
  listeners.forEach((l) => l());
}

let current = typeof window === "undefined" ? false : read();

export function useShowTechnical(): boolean {
  return React.useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => false,
  );
}

/** Renders its children only while technical details are switched on. */
export function Technical({ children, fallback = null }: { children: React.ReactNode; fallback?: React.ReactNode }) {
  return <>{useShowTechnical() ? children : fallback}</>;
}

/** The switch itself — a labelled toggle, for an account menu or a settings page. */
export function TechnicalToggle({ className }: { className?: string }) {
  const on = useShowTechnical();
  return (
    <label className={cn("flex min-h-10 cursor-pointer items-center justify-between gap-3 text-[13px] text-fg", className)}>
      <span>
        Show technical details
        <span className="block text-[11.5px] text-muted">Server names, system ids and raw values</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Show technical details"
        onClick={() => setShowTechnical(!on)}
        className={cn(
          "relative h-6 w-10 shrink-0 rounded-full border transition-colors",
          on ? "border-transparent bg-accent" : "border-line2 bg-surface3",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 size-[18px] rounded-full bg-white shadow transition-[left]",
            on ? "left-[18px]" : "left-0.5",
          )}
        />
      </button>
    </label>
  );
}
